import { Button, BUTTON_TYPES } from '../../../components/Button'
import { BTN_TXT } from '../../../constants'
import { useDashboardFilterContext } from '../Providers/DashboardFilterContendProvider'
import { ReactComponent as Close16 } from '../../../assets/svg/Clos16.svg'
import { Chips } from '../../../components/Chips/Chips'
import './styles.scss'
import { FILTERS_TITLES } from './constants'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export const AppliedFiltersBlock = () => {
  const {
    state: { filtersApplied, filtersWithOptionsName },
    actions: { resetFilters, onFilterItemRemove }
  } = useDashboardFilterContext()

  const commonChipsCount = filtersWithOptionsName.length

  const chipsGap = 8
  const containerRef = useRef<HTMLDivElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  const [visibleChipsCount, setVisibleChipsCount] = useState(0)
  const [expanded, setExpanded] = useState(false)
  const [containerWidth, setContainerWidth] = useState(0)

  useEffect(() => {
    // show all chips firstly
    if (commonChipsCount && !visibleChipsCount) {
      setVisibleChipsCount(commonChipsCount)
    }
  }, [commonChipsCount, visibleChipsCount])

  useEffect(() => {
    if (visibleChipsCount && !containerWidth) {
      containerRef.current = document.querySelector('.tags-component')
      // predefine width - if it is not set yet
      setContainerWidth(containerRef.current?.offsetWidth ?? 0)
      resizeObserverRef.current = new ResizeObserver((_entries) => {
        for (const _entry of _entries) {
          setContainerWidth(_entry.contentRect.width)
        }
      })

      if (containerRef.current)
        resizeObserverRef.current.observe(containerRef.current)
    }
  }, [containerWidth, visibleChipsCount])

  useEffect(() => {
    return () => resizeObserverRef.current?.disconnect()
  }, [])

  const onReset = useCallback(() => {
    resetFilters()
    setExpanded(false)
  }, [resetFilters])

  useEffect(() => {
    if (!expanded && containerRef.current) {
      if (!visibleChipsCount || !containerWidth) return
      const _children = Array.from(containerRef.current.children).filter(
        (el) => el.tagName !== 'button'
      ) as HTMLElement[]
      let _usedWidth = 60 // plus button width
      let _count = 0

      _children.every((el) => {
        const _elWidth = el.offsetWidth + chipsGap

        if (_usedWidth + _elWidth <= containerWidth) {
          _usedWidth += _elWidth
          _count++
          return true
        } else return false
      })

      setVisibleChipsCount(_count)
    } else {
      setVisibleChipsCount(commonChipsCount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonChipsCount, containerWidth, expanded, chipsGap])

  const hiddenCount = useMemo(() => {
    return commonChipsCount - visibleChipsCount
  }, [commonChipsCount, visibleChipsCount])

  if (!filtersApplied) return null

  return (
    <div className="row space-between align-start">
      <div className={`tags-component flex gap-${chipsGap}`}>
        {filtersWithOptionsName
          .slice(0, visibleChipsCount)
          .map(({ name, value, key }) => (
            <Chips
              className="tags-component__filter-item"
              key={value}
              closable
              onClose={() => onFilterItemRemove(key, value)}
            >
              {FILTERS_TITLES[key]} : {name}
            </Chips>
          ))}
        {!expanded && hiddenCount > 0 && (
          <Button
            type={BUTTON_TYPES.PRIMARY}
            onClick={() => setExpanded(true)}
            className="hidden-count-button"
          >
            +{hiddenCount}
          </Button>
        )}
        {expanded &&
          filtersWithOptionsName
            .slice(visibleChipsCount)
            .map(({ key, value, name }) => (
              <Chips
                className="tags-component__filter-item"
                key={value}
                closable
                onClose={() => onFilterItemRemove(key, value)}
              >
                {FILTERS_TITLES[key]} : {name}
              </Chips>
            ))}
        {expanded && (
          <Button type={BUTTON_TYPES.LINK} onClick={() => setExpanded(false)}>
            Show less
          </Button>
        )}
      </div>
      <Button
        type={BUTTON_TYPES.GHOST}
        onClick={onReset}
        upperCase
        className="filters-button-reset"
        icon={<Close16 />}
      >
        {BTN_TXT.RESET_FILTERS}
      </Button>
    </div>
  )
}
